import { Moment } from 'moment';

import { ILog } from '@app/dashboard/logs/log-view-model';
import { DRIVER_SUGGESTED_BY, DRIVER_SUGGESTED_BY_MAPPING, IServerSuggestedDriver } from '@app/services/trips-list';

export interface IComplianceTagBucket {
  key: ComplianceTagsBucket,
  label: string,
  tooltip: string,
  tags: ComplianceCoachableBehaviorType[],
  color: string,
}

export interface IComplianceEventTypes extends IComplianceShiftViolationEventType, IComplianceFormAndMannerErrorEventType {

}

export interface IComplianceShiftViolationEventType {
  shift_violations: ComplianceTag,
  cycle_violations: ComplianceTag,
  driving_violations: ComplianceTag,
  break_violations: ComplianceTag,
  off_duty_violations: ComplianceTag,
  exception_violations: ComplianceTag,
}

export interface IComplianceFormAndMannerErrorEventType {
  signature: ComplianceTag,
  shipping_info: ComplianceTag,
  carrier_address: ComplianceTag,
  miles: ComplianceTag,
  tractor_num: ComplianceTag,
  form_and_manner_error: ComplianceTag,
  odometers: ComplianceTag,
  carrier_name: ComplianceTag,
}

export type ComplianceCoachableBehaviorType = keyof IComplianceEventTypes;

export type ComplianceTag = string;

export const COMPLIANCE_SHIFT_VIOLATIONS: IComplianceShiftViolationEventType = {
  shift_violations: 'shift_violations',
  cycle_violations: 'cycle_violations',
  driving_violations: 'driving_violations',
  break_violations: 'break_violations',
  off_duty_violations: 'off_duty_violations',
  exception_violations: 'exception_violations',
};

export const COMPLIANCE_FORM_AND_MANNER_ERROR_VIOLATIONS: IComplianceFormAndMannerErrorEventType = {
  signature: 'signature',
  shipping_info: 'shipping_info',
  carrier_address: 'carrier_address',
  miles: 'miles',
  tractor_num: 'tractor_num',
  form_and_manner_error: 'fm_errors',
  odometers: 'odometers',
  carrier_name: 'carrier_name',
};

export const COMPLIANCE_EVENT_TYPES: IComplianceEventTypes = {
  ...COMPLIANCE_SHIFT_VIOLATIONS,
  ...COMPLIANCE_FORM_AND_MANNER_ERROR_VIOLATIONS,
};

export const COMPLIANCE_EVENT_TYPES_LABELS: IComplianceEventTypes = {
  shift_violations: $localize`Shift violations`,
  cycle_violations: $localize`Cycle violations`,
  driving_violations: $localize`Driving violations`,
  break_violations: $localize`Break violations`,
  off_duty_violations: $localize`Off-duty violations`,
  exception_violations: $localize`Exception violations`,
  signature: $localize`Driver signature errors`,
  shipping_info: $localize`Shipping docs errors`,
  carrier_address: $localize`Main office errors`,
  miles: $localize`Distance errors`,
  tractor_num: $localize`Vehicle errors`,
  form_and_manner_error: $localize`Form and manner errors`,
  odometers: $localize`Odometer errors`,
  carrier_name: $localize`Carrier name errors`,
};

export interface IServerCategorizedViolations {
  form_and_manner: string[];
  hos: string[];
}

export const COMPLIANCE_COACHABLE_BEHAVIORS: IComplianceEventTypes = {
  shift_violations: COMPLIANCE_EVENT_TYPES.shift_violations,
  cycle_violations: COMPLIANCE_EVENT_TYPES.cycle_violations,
  driving_violations: COMPLIANCE_EVENT_TYPES.driving_violations,
  break_violations: COMPLIANCE_EVENT_TYPES.break_violations,
  off_duty_violations: COMPLIANCE_EVENT_TYPES.off_duty_violations,
  exception_violations: COMPLIANCE_EVENT_TYPES.exception_violations,
  signature: COMPLIANCE_EVENT_TYPES.signature,
  shipping_info: COMPLIANCE_EVENT_TYPES.shipping_info,
  carrier_address: COMPLIANCE_EVENT_TYPES.carrier_address,
  miles: COMPLIANCE_EVENT_TYPES.miles,
  tractor_num: COMPLIANCE_EVENT_TYPES.tractor_num,
  form_and_manner_error: COMPLIANCE_EVENT_TYPES.form_and_manner_error,
  odometers: COMPLIANCE_EVENT_TYPES.odometers,
  carrier_name: COMPLIANCE_EVENT_TYPES.carrier_name,
};

export const COMPLIANCE_COACHABLE_BEHAVIORS_LABELS: IComplianceEventTypes= {
  shift_violations: COMPLIANCE_EVENT_TYPES_LABELS.shift_violations,
  cycle_violations: COMPLIANCE_EVENT_TYPES_LABELS.cycle_violations,
  driving_violations: COMPLIANCE_EVENT_TYPES_LABELS.driving_violations,
  break_violations: COMPLIANCE_EVENT_TYPES_LABELS.break_violations,
  off_duty_violations: COMPLIANCE_EVENT_TYPES_LABELS.off_duty_violations,
  exception_violations: COMPLIANCE_EVENT_TYPES_LABELS.exception_violations,
  signature: COMPLIANCE_EVENT_TYPES_LABELS.signature,
  shipping_info: COMPLIANCE_EVENT_TYPES_LABELS.shipping_info,
  carrier_address: COMPLIANCE_EVENT_TYPES_LABELS.carrier_address,
  miles: COMPLIANCE_EVENT_TYPES_LABELS.miles,
  tractor_num: COMPLIANCE_EVENT_TYPES_LABELS.tractor_num,
  form_and_manner_error: COMPLIANCE_EVENT_TYPES_LABELS.form_and_manner_error,
  odometers: COMPLIANCE_EVENT_TYPES_LABELS.odometers,
  carrier_name: COMPLIANCE_EVENT_TYPES_LABELS.carrier_name,
};

export enum CANADIAN_CYCLES {
  '70_7' = '70_7',
  '120_14' = '120_14',
  '80_7' = '80_7',
  '120_14_north' = '120_14_north',
}

enum DeferredDay {
  'rejected', 'today', 'nextDay',
}

export enum ASSIGNMENT_FILTER_OPTIONS {
  UNIDENTIFIED = 'unidentified',
  AI_IDENTIFIED = 'ai_identified',
  AI_ASSIGNED = 'ai_assigned',
  MANUALLY_ASSIGNED = 'manually_assigned',
  DRIVER_CLAIMED = 'driver_claimed',
  DRIVER_CONNECTED = 'driver_connected',
  RFID = 'rfid',
}

export enum DRIVER_STATUS {
  UNIDENTIFIED = 'UNIDENTIFIED',
  IDENTIFIED = 'IDENTIFIED',
  NON_COMPANY = 'NON-COMPANY',
}

export enum ComplianceTagsBucket {
  hos_violations = 'hos_violations',
  form_and_manner_errors = 'fm_errors',
}

type ComplianceTagsBucketLabel = { [key in ComplianceTagsBucket]: string };
export type ComplianceTagsBucketCount = { [key in ComplianceTagsBucket]: number };

export const COMPLIANCE_TAGS_BUCKET_LABELS: ComplianceTagsBucketLabel = {
  hos_violations: $localize`HOS violations`,
  fm_errors: $localize`Form & manner errors`,
};

export enum LOG_EVENT_TYPES {
  DRIVING = 'driving',
  OFF_DUTY = 'off_duty',
  ON_DUTY = 'on_duty',
  SLEEPER = 'sleeper',
  WAITING = 'waiting',
}

export enum IGNORE_DRIVING_STATUSES {
  OFF_DUTY_PC = 'off_duty',
  ON_DUTY_YM = 'on_duty',
  WAITING = 'waiting',
}

export enum DOCUMENT_CLASSIFICATION_VALUES {
  TRIP = 'trip',
  DRIVER_QUALIFICATION = 'driver',
  VEHICLE = 'vehicle',
  OTHER = 'other',
}

export const DOCUMENT_CLASSIFICATION_LABEL = {
  [DOCUMENT_CLASSIFICATION_VALUES.TRIP] : $localize`Trip`,
  [DOCUMENT_CLASSIFICATION_VALUES.DRIVER_QUALIFICATION]: $localize`Driver Qualification`,
  [DOCUMENT_CLASSIFICATION_VALUES.VEHICLE]: $localize`Vehicle`,
  [DOCUMENT_CLASSIFICATION_VALUES.OTHER]: $localize`Other`,
};

export const IGNORE_DRIVE_TIME_MAPPING = {
  [IGNORE_DRIVING_STATUSES.OFF_DUTY_PC]: $localize`Off Duty(PC)`,
  [IGNORE_DRIVING_STATUSES.ON_DUTY_YM]: $localize`On Duty(YM)`,
  [IGNORE_DRIVING_STATUSES.WAITING]: $localize`Waiting`,
};

export const COMPLIANCE_EVENTS_BUCKET: IComplianceTagBucket[] = [
  {
    key: ComplianceTagsBucket.hos_violations,
    label: COMPLIANCE_TAGS_BUCKET_LABELS.hos_violations,
    tooltip: null,
    tags: [
      COMPLIANCE_SHIFT_VIOLATIONS.shift_violations as keyof IComplianceEventTypes,
      COMPLIANCE_SHIFT_VIOLATIONS.cycle_violations as keyof IComplianceEventTypes,
      COMPLIANCE_SHIFT_VIOLATIONS.driving_violations as keyof IComplianceEventTypes,
      COMPLIANCE_SHIFT_VIOLATIONS.break_violations as keyof IComplianceEventTypes,
      COMPLIANCE_SHIFT_VIOLATIONS.off_duty_violations as keyof IComplianceEventTypes,
      COMPLIANCE_SHIFT_VIOLATIONS.exception_violations as keyof IComplianceEventTypes,
    ],
    color: null,
  },
  {
    key: ComplianceTagsBucket.form_and_manner_errors,
    label: COMPLIANCE_TAGS_BUCKET_LABELS.fm_errors,
    tooltip: null,
    tags: [
      COMPLIANCE_FORM_AND_MANNER_ERROR_VIOLATIONS.signature as keyof IComplianceEventTypes,
      COMPLIANCE_FORM_AND_MANNER_ERROR_VIOLATIONS.shipping_info as keyof IComplianceEventTypes,
      COMPLIANCE_FORM_AND_MANNER_ERROR_VIOLATIONS.carrier_address as keyof IComplianceEventTypes,
      COMPLIANCE_FORM_AND_MANNER_ERROR_VIOLATIONS.miles as keyof IComplianceEventTypes,
      COMPLIANCE_FORM_AND_MANNER_ERROR_VIOLATIONS.tractor_num as keyof IComplianceEventTypes,
      COMPLIANCE_FORM_AND_MANNER_ERROR_VIOLATIONS.odometers as keyof IComplianceEventTypes,
      COMPLIANCE_FORM_AND_MANNER_ERROR_VIOLATIONS.carrier_name as keyof IComplianceEventTypes,
    ],
    color: null,
  },
];

export function validateCanadianCycleSwitch(currentLogCycle: string, newCycle: string, recentBreakDuration: number): number {
  if ((currentLogCycle === CANADIAN_CYCLES['70_7'] && newCycle === CANADIAN_CYCLES['120_14']) ||
    (currentLogCycle === CANADIAN_CYCLES['80_7'] && newCycle === CANADIAN_CYCLES['120_14_north'])
  ) {
    // cycle 1 is selected
    return 129600 - recentBreakDuration; // 36 * 60 * 60
  } else if ((newCycle === CANADIAN_CYCLES['70_7'] && currentLogCycle === CANADIAN_CYCLES['120_14']) ||
    (newCycle === CANADIAN_CYCLES['80_7'] && currentLogCycle === CANADIAN_CYCLES['120_14_north'])
  ) {
    // cycle 2 is selected
    return 259200 - recentBreakDuration; // 72 * 60 * 60
  } else {
    return null;
  }
}

export function generateDeferredExceptionText(deferredTime: string, log: ILog): string {
  const logDate: Moment = log.date.clone();
  const isDeferredToday = log.deferralDay === DeferredDay.today;
  const dayToShow: Moment = isDeferredToday ? logDate.add(1, 'day') : logDate.subtract(1, 'day');
  const offDutyDeferredText = $localize`Off Duty Deferred`;
  const to = $localize`to`;
  const from = $localize`from`;

  return `${deferredTime} ${offDutyDeferredText}
    ${isDeferredToday ? to : from} ${dayToShow.format('MMM DD')}`
  ;
}

export function generateReducedRestExceptionText(reducedRestTime: string, log: ILog): string {
  const logDate: Moment = log.date.clone();
  const isReducedRestToday = log.reducedRestDay === DeferredDay.today;
  const dayToShow: Moment = isReducedRestToday ? logDate.add(1, 'day') : logDate.subtract(1, 'day');
  const offDutyReducedRestText = $localize`Reduced Rest -`;
  const movedTo = $localize`moved to`;
  const addedFrom = $localize`added from`;

  return `${offDutyReducedRestText} ${reducedRestTime}
    ${isReducedRestToday ? movedTo : addedFrom} ${dayToShow.format('MMM DD')}`
  ;
}

export function doesPCLimitApply(pcLmitJurisdiction: string, logs: any[]): boolean {
  switch (pcLmitJurisdiction) {
  case 'none': return false;
  case 'canada': return logs.filter(log => log.hasCanadaCycle).length > 0;
  case 'all': return true;
  default: return false;
  }
}

export const transformTooltipData = (data: string[] = [], info: string): string[] => {
  if (!data.length) {
    return [];
  }
  
  if (data.length <= 13) {
    return [...data];
  }
  
  // For more than 13 groups, show the first 13 and a count of remaining
  const visibleData = data.slice(0, 13);
  const remainingCount = data.length - 13;
  
  return [
    ...visibleData,
    $localize`${remainingCount} more ${info}${remainingCount > 1 ? 's' : ''}` // pluralize if more than one
  ];
};

export function shouldNotShowSTMSuggestion(suggestedDriverServer: IServerSuggestedDriver, isSTMEnabled: boolean): boolean {
  return DRIVER_SUGGESTED_BY_MAPPING[suggestedDriverServer?.suggested_by] === DRIVER_SUGGESTED_BY.SMART_TRIP_MATCHING &&
    !isSTMEnabled
  ;
}

export function mapSdsEventType(status): string {
  status = status.toLowerCase();
  switch (status) {
  case 'ym':
    return $localize`Yard Move`;
  case 'pc':
    return $localize`Personal Conveyance`;
  }
}

export enum TRIP_STATUSES_VALUES {
  DRIVING = 'driving',
  YM = 'ym',
  PC = 'pc',
}